import { useState, useCallback } from 'react';

import { useBoolean } from '@chakra-ui/react';

import { parse, ParseResult } from 'papaparse';

import { useToast } from '@aslanpay/portal-common-lib';

import { AETFileMetadata, FileType } from 'app/components/Employees/ImportEmployees/types';

interface ParsedData<T> {
  data: T[];
  error: Error | null;
  file: File | null;
  metadata?: AETFileMetadata;
}

const useReadImportCSV = <T>(columns: string[], fileType: FileType) => {
  const toast = useToast();

  const [parsedData, setParsedData] = useState<ParsedData<T>>({
    data: [],
    error: null,
    file: null,
  });

  const [isParsingCSV, setIsParsingCSV] = useBoolean();

  const parseAETFile = (file: File) => {
    // For AET files, we need to parse the metadata rows and then parse the data
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => {
          // Remove empty lines and lines that are only commas
          return line.length > 0 && !line.match(/^,+$/);
        });

      if (lines.length < 4) {
        const errorMessage = `AET file must have at least 4 rows (3 metadata + 1 header + data). Found only ${lines.length} rows.`;

        setIsParsingCSV.toggle();
        setParsedData({
          data: [],
          error: Error(errorMessage),
          file: null,
        });
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Invalid AET File Structure',
          description: errorMessage,
          duration: 8000,
        });
        return;
      }

      // Find the metadata rows (Pay Calendar, Start Date, End Date)
      let payCalendarLine = -1;
      let startDateLine = -1;
      let endDateLine = -1;
      let headerLine = -1;

      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];

        if (line.startsWith('Pay Calendar,')) {
          payCalendarLine = i;
        } else if (line.startsWith('Start Date,')) {
          startDateLine = i;
        } else if (line.startsWith('End Date,')) {
          endDateLine = i;
        } else if (line.startsWith('Payroll ID,')) {
          headerLine = i;
        }
      }

      if (
        payCalendarLine === -1 ||
        startDateLine === -1 ||
        endDateLine === -1 ||
        headerLine === -1
      ) {
        const missingRows = [];
        if (payCalendarLine === -1) missingRows.push('Pay Calendar');
        if (startDateLine === -1) missingRows.push('Start Date');
        if (endDateLine === -1) missingRows.push('End Date');
        if (headerLine === -1) missingRows.push('Payroll ID');

        const errorMessage = `AET file is missing required rows: ${missingRows.join(
          ', '
        )}. Please use the AET template file.`;

        setIsParsingCSV.toggle();
        setParsedData({
          data: [],
          error: Error(errorMessage),
          file: null,
        });
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Missing Required Rows',
          description: errorMessage,
          duration: 8000,
        });
        return;
      }

      // Parse metadata from the found rows
      const metadata: AETFileMetadata = {
        payCalendarId: lines[payCalendarLine].split(',')[1]?.trim() || '',
        payCycleStartDate: lines[startDateLine].split(',')[1]?.trim() || '',
        payCycleEndDate: lines[endDateLine].split(',')[1]?.trim() || '',
        startDate: lines[startDateLine].split(',')[1]?.trim() || '',
        endDate: lines[endDateLine].split(',')[1]?.trim() || '',
        employeeEarningsAdded: [],
        employeeEarningsChanged: [],
        employeeEarningsDeleted: [],
      };

      // Debug logging removed for production

      // Create a new CSV string starting from the header row
      const dataLines = lines.slice(headerLine);
      const csvWithoutMetadata = dataLines.join('\n');

      // Parse the remaining data
      parse<T>(csvWithoutMetadata, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        delimiter: ',',
        complete: (results: ParseResult<T>) => {
          const { data } = results;

          const headerColumns = Object.keys(data[0] || {});

          // Debug logging removed for production

          const missingColumns = columns.filter(
            (column) =>
              !headerColumns.some((col) => col.toLowerCase().trim() === column.toLowerCase().trim())
          );

          setIsParsingCSV.toggle();

          if (missingColumns.length === 0) {
            setParsedData({
              data,
              error: null,
              file,
              metadata,
            });
          } else {
            const errorMessage = `AET file is missing required columns: ${missingColumns.join(
              ', '
            )}. Please use the AET template file provided.`;

            setParsedData({
              data: [],
              error: Error(errorMessage),
              file: null,
            });

            toast({
              position: 'top-right',
              status: 'error',
              title: 'Missing Required Columns',
              description: errorMessage,
              duration: 8000,
            });
          }
        },
        error: (error: Error) => {
          setIsParsingCSV.toggle();

          setParsedData({
            data: [],
            error,
            file: null,
          });

          toast({
            position: 'top-right',
            status: 'error',
            title: 'File Parse Error',
            description: `Failed to parse AET file: ${error.message}. Please check the file format and try again.`,
            duration: 8000,
          });
        },
      });
    };

    reader.readAsText(file);
  };

  const parseEDFFile = (file: File) => {
    parse<T>(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimiter: ',',
      complete: (results: ParseResult<T>) => {
        const { data } = results;

        const headerColumns = Object.keys(data[0] || {});

        const missingColumns = columns.filter(
          (column) =>
            !headerColumns.some((col) => col.toLowerCase().trim() === column.toLowerCase().trim())
        );

        setIsParsingCSV.toggle();

        if (missingColumns.length === 0) {
          setParsedData({
            data,
            error: null,
            file,
          });
        } else {
          const errorMessage = `Employee file is missing required columns: ${missingColumns.join(
            ', '
          )}. Please use the employee template file provided.`;

          setParsedData({
            data: [],
            error: Error(errorMessage),
            file: null,
          });

          toast({
            position: 'top-right',
            status: 'error',
            title: 'Missing Required Columns',
            description: errorMessage,
            duration: 8000,
          });
        }
      },
      error: (error) => {
        setIsParsingCSV.toggle();

        setParsedData({
          data: [],
          error,
          file: null,
        });

        toast({
          position: 'top-right',
          status: 'error',
          title: 'File Parse Error',
          description: `Failed to parse employee file: ${error.message}. Please check the file format and try again.`,
          duration: 8000,
        });
      },
    });
  };

  const parseCSV = (file: File) => {
    setIsParsingCSV.toggle();

    if (fileType === 'APPROVED_EARNINGS_TIMESHEET') {
      parseAETFile(file);
    } else {
      parseEDFFile(file);
    }
  };

  const resetParsedData = useCallback(() => {
    setParsedData({
      data: [],
      error: null,
      file: null,
    });
  }, []);

  return { parsedData, parseCSV, isParsingCSV, resetParsedData };
};

export default useReadImportCSV;
